import { createAction } from 'redux-actions';
import { SEND_MESSAGE } from 'mxp-graphql-queries';
import { default as request } from 'utils/GraphQLClient';
import { Dispatch } from 'redux';
import { getLocalUser, getUser, getUserLocation, getUserState } from 'modules/user/actions';
import { getLocalCheckout } from 'modules/checkout/actions';
import { getCurrency } from 'modules/products/actions';
import { getCart, getCartOrMigrate, updatePreferredCurrencyByCart } from 'modules/cart';

import { getSupervisorDashboard } from 'modules/cimasupervisor/actions';
import * as actions from 'modules/postLoginActionsTable';
import { StorageNames } from 'constants/index';
import { isServer } from 'utils';
import { getSessionStorageItem, removeSessionStorageItem } from 'utils/sessionStorage';
import { appInitializedSelector } from 'modules/app/selectors';
import { User as UserTypes, Cart } from 'mxp-schemas';
import { getPracticalExperienceRequirement, setMembershipEvents } from 'modules/membership/actions';
import { analyticsLayer } from 'middlewares/analytics';

// ------------------------------------
// Actions
// ------------------------------------
export const resetAppInitialized: any = createAction('app/RESET_APP_INITIALIZED');

export const loadConstants: any = createAction('app/LOAD_CONSTANTS');

export const sendMessage: any = createAction('app/SEND_LOG', (message: string) => () => {
  return request(SEND_MESSAGE, { message });
});

export const rehydrateUserData: any = createAction(
  'app/HYDRATE_USER_DATA',
  (isAuth: boolean, session?: string) => async (dispatch: Dispatch, getState: () => State.Root) => {
    const state = getState();

    // after modal window user login rehydrateUserData fired again and appInitialized must be reset
    if (appInitializedSelector(state)) dispatch(resetAppInitialized());

    if (isServer) {
      // This action is only relevant for the client side rendering, do nothing on the server
      return Promise.resolve();
    }

    // anonymous user rehydration
    if (!isAuth) {
      await dispatch(getUserLocation());
      // just get anon cart
      if (session === UserTypes.SessionType.EXTERNAL_SESSION) {
        dispatch(getSupervisorDashboard(false));
      }
      return dispatch(getCart());
      // do not proceed for non Auth;
    }

    // Logged user rehydration
    dispatch(getLocalUser());
    dispatch(getLocalCheckout());
    dispatch(getUser());

    const actionsInProgress: any = [];

    // check if there's an existing purchase
    await dispatch(getCurrency());
    await dispatch(getUserLocation());
    await dispatch(updatePreferredCurrencyByCart());

    // get currency first before migrating cart if there's one so we can pass the correct one.
    const cartResult = await dispatch(getCartOrMigrate());

    // Push appInitialized to GTM dataLayer for existing sessions.
    const freshState = getState();
    const userData = freshState.user?.data;
    if (isAuth && userData) {
      analyticsLayer.appInitialized(
        userData.roles?.[0] || '',
        userData.userId || '',
        userData.AICPAUID || '',
        userData.roles || []
      );
    }

    const cart = cartResult?.payload?.payload;

    // Retrieve stored state for this cart and apply it, in case it was cleared by a refresh during checkout
    const remoteUserState = (await dispatch(getUserState()))?.payload ?? {};
    const cartState = remoteUserState[StorageNames.activeCart] as Cart.ActiveCart | null;

    const isClickedMembershipUpgradeFromSession =
      getSessionStorageItem(StorageNames.isClickedMembershipUpgrade) === true;
    if (isClickedMembershipUpgradeFromSession) {
      dispatch(
        setMembershipEvents({
          isClickedMembershipUpgrade: true,
        })
      );
    }

    const hasValidCartState = cartState && cart?.id === cartState.cartId && cart?.lineItems?.length;

    if (cartState && hasValidCartState) {
      const existingEvents = state.membership.events;
      dispatch(
        setMembershipEvents({
          isClickedMembershipUpgrade: cartState.isClickedMembershipUpgrade ?? isClickedMembershipUpgradeFromSession,
          isClickedCimaMembershipRenewalInProfile:
            cartState.isClickedCimaMembershipRenewalInProfile || existingEvents.isClickedCimaMembershipRenewalInProfile,
          isClickedCimaMembershipRenewalInFeed:
            cartState.isClickedCimaMembershipRenewalInFeed || existingEvents.isClickedCimaMembershipRenewalInFeed,
          isClickedMembershipRenewal: cartState.isClickedMembershipRenewal || existingEvents.isClickedMembershipRenewal,
          isClickedFlpMembershipRenewal:
            cartState.isClickedFlpMembershipRenewal || existingEvents.isClickedFlpMembershipRenewal,
        })
      );
      if (cartState.isPERCompleted) {
        await dispatch(getPracticalExperienceRequirement());
      }
      if (cartState.currentLearningPathway) {
        dispatch(actions.setCurrentLearningPathway(cartState.currentLearningPathway));
      }
    }

    // dispatch actions that happened before login
    const actionsToDispatchAfterLogin: any = getSessionStorageItem(StorageNames.actionsToDispatchAfterLogin) || [];
    if (actionsToDispatchAfterLogin.length) {
      actionsToDispatchAfterLogin.forEach((action: { type: string; payload: any }) => {
        const actionCreator: any = Object.values(actions).find(creator => creator().type === action.type);
        if (!actionCreator) return;

        actionsInProgress.push(dispatch(actionCreator(...action.payload)));
      });
    }
    removeSessionStorageItem(StorageNames.actionsToDispatchAfterLogin);

    return Promise.all(actionsInProgress);
  }
);
